var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Operation as LogOperation, Log, LogCategory } from '@augloop/logging';
import { createConnectionReliabilityManager } from './iconnection-reliability-manager';
import Deque from 'double-ended-queue';
// Rate control thresholds for logging purpose
const rpsLoggingThreshold = 50;
const bpsLoggingThreshold = 100000;
// Rate control maximum sizes
const pendingMaxSize = 10000;
const egressedMaxSize = 10000;
const messageMaxSize = 3000000; //3MB imposed by server
// Parameters for dynamic rate controller
const rateControllerIntervalMs = 1000;
const maxQueueSize = 10000;
const rateThrottlePct = 0.9; // 90% of max rps/bps capacities retrieved from server
export class NetworkRateController {
    //
    constructor(networkRateControllerOptions) {
        var _a;
        this.emptyMessageId = 0;
        this.egressMessageCount = 0;
        this.egressByteCount = 0;
        this.prevSeq = -1;
        this.pendingQueue = [];
        // Default rate control thresholds to ensure max egress rate
        this.rpsThreshold = 150;
        this.bpsThreshold = 500000000; // 500MB;
        this.isClosing = false;
        this.messageQueue = undefined;
        this.egressRateLogOp = new LogOperation({ operationName: 'NetworkEgressRate', success: true });
        this.egressedCache = new Map();
        this.initPromise = new Promise((resolve) => { this.resolveInitPromise = resolve; });
        // Dynamic rate controller
        this.dynamicRateControllerEnabled = (_a = networkRateControllerOptions === null || networkRateControllerOptions === void 0 ? void 0 : networkRateControllerOptions.dynamicRateControllerEnabled) !== null && _a !== void 0 ? _a : false;
        if (this.dynamicRateControllerEnabled) {
            this.messageQueue = new Deque();
        }
    }
    init(worker, ingress, queryEgressCacheSize, networkMode, clientMetadata) {
        this.worker = worker;
        this.ingress = ingress;
        this.queryEgressCacheSize = queryEgressCacheSize;
        this.clientMetadata = clientMetadata;
        this.networkMode = networkMode;
        this.reliabilityManager = createConnectionReliabilityManager(this.worker, this.close.bind(this));
        this.egressRateLogOp.setClientMetadata(this.clientMetadata);
        if (this.dynamicRateControllerEnabled) {
            // Dynamic rate controller
            this.resetRateLimiter();
            this.onCloseController = new Promise((resolve) => {
                this.resolveCloseControllerPromise = resolve;
            });
            this.queueProcessingCompletePromise = new Promise((resolve) => {
                this.resolveQueueProcessingCompletePromise = resolve;
            });
            void this.processQueue();
        }
        this.resolveInitPromise();
    }
    open() {
        const rateControllerOpen = new LogOperation({ operationName: 'NetworkRateControllerOpen', success: true, dimension3: `networkMode: ${this.networkMode}` }).start();
        rateControllerOpen.setClientMetadata(this.clientMetadata);
        this.initPromise.then(() => {
            if (!this.reliabilityManager) {
                rateControllerOpen.success = false;
                rateControllerOpen.resultDescription = 'Reliability Manager is undefined';
                return;
            }
            this.egressRateControlIntervalStart = Date.now();
            this.reliabilityManager.start();
            if (!this.dynamicRateControllerEnabled) {
                this.egressRateControlTimer = setInterval(() => this.flush(), 1000);
                this.flush();
            }
        }).catch((error) => {
            var _a;
            rateControllerOpen.success = false;
            rateControllerOpen.resultDescription = (_a = 'Catch: ' + (error === null || error === void 0 ? void 0 : error.message)) !== null && _a !== void 0 ? _a : '';
        }).finally(() => {
            Log.info(0x1e44f010 /* tag_4rpaq */, LogCategory.CoreDefault, rateControllerOpen.stop());
        });
    }
    ingressFromWorker(message, onIngressParsed) {
        if (!this.reliabilityManager) {
            this.ingress(message, onIngressParsed);
            return;
        }
        if (!this.reliabilityManager.needsParsedResponses() && this.reliabilityManager.isReliabilityResponse(message)) {
            // If we don't need parsed responses, we can call onResponse before parsing
            // Ping pong message is just a string and doesn't need to be parsed
            this.reliabilityManager.onResponse();
            return;
        }
        this.ingress(message, (response) => {
            onIngressParsed === null || onIngressParsed === void 0 ? void 0 : onIngressParsed(response);
            if (this.reliabilityManager.needsParsedResponses() && this.reliabilityManager.isReliabilityResponse(response)) {
                // If we are using http, we need to parse the message to see if it is a reliability response
                this.reliabilityManager.onResponse(response);
            }
        });
    }
    onRateLimitErrorResponse(err) {
        if (this.dynamicRateControllerEnabled) {
            const logOp = new LogOperation({ operationName: 'NetworkRateControllerOnRateLimitResponse', success: true, }).start();
            logOp.setClientMetadata(this.clientMetadata);
            logOp.resultSignature = `rateLimitAlreadyStarted: ${this.rateLimitTimeout !== undefined}`;
            logOp.setDataField('RetryAfterMs', err.retryAfterMs);
            logOp.setDataField('QueueSize', this.messageQueue.length);
            this.startRateLimiting(err.retryAfterMs);
            Log.info(0x1e3e230c /* tag_4p8mm */, LogCategory.CoreDefault, logOp.stop());
        }
    }
    setRpsBps(maxRPS, maxBPS) {
        const logOp = new LogOperation({ operationName: 'NetworkRateControllerRateLimitsSet', success: true }).start();
        logOp.setClientMetadata(this.clientMetadata);
        if (maxRPS) {
            this.rpsThreshold = rateThrottlePct * maxRPS;
            logOp.resultDescription += `maxRPS: ${maxRPS}, rpsThreshold: ${this.rpsThreshold}; `;
        }
        if (maxBPS) {
            this.bpsThreshold = rateThrottlePct * maxBPS;
            logOp.resultDescription += `maxRPS: ${maxBPS}, rpsThreshold: ${this.bpsThreshold}; `;
        }
        Log.info(0x1e3e230b /* tag_4p8ml */, LogCategory.CoreDefault, logOp.stop());
    }
    close() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const rateControllerClose = new LogOperation({ operationName: 'NetworkRateControllerClose', success: true, dimension3: `networkMode: ${this.networkMode}` }).start();
            rateControllerClose.setClientMetadata(this.clientMetadata);
            this.isClosing = true;
            // Dynamic rate controller
            if (this.dynamicRateControllerEnabled) {
                (_a = this.resolveCloseControllerPromise) === null || _a === void 0 ? void 0 : _a.call(this);
                const isConnected = (_b = this.reliabilityManager) === null || _b === void 0 ? void 0 : _b.checkConnection();
                rateControllerClose.setDataField('QueueSizeBeforeFlush', this.messageQueue.length);
                if (this.messageQueue.length != 0 && isConnected) {
                    yield this.queueProcessingCompletePromise;
                }
                rateControllerClose.setDataField('QueueSizeAfterFlush', this.messageQueue.length);
                rateControllerClose.setDataField('IsConnected', isConnected);
            }
            if (!this.reliabilityManager) {
                rateControllerClose.resultDescription = 'Reliability Manager is undefined';
            }
            else {
                this.reliabilityManager.close();
            }
            this.worker = undefined;
            this.prevSeq = -1;
            this.clearEgressControlTimeout();
            this.reliabilityManager = undefined;
            Log.info(0x1e44f00d /* tag_4rpan */, LogCategory.CoreDefault, rateControllerClose.stop());
        });
    }
    clearMessageQueues() {
        this.pendingQueue = [];
        this.egressedCache.clear();
        this.messageQueue = undefined;
    }
    /**
     * Receives message to be sent from upstream. Send it if possible, else put it in pending queue
     *
     * @param message message to be sent, containing seqId, messageId, and isSessionInitMessage flag
     * @returns None
     */
    egress(message, attemptNumber = 0) {
        var _a, _b, _c;
        if (!this.reliabilityManager) {
            this.logEgressActivity(false /*success*/, 'Reliability Manager is undefined', '' /*resultSignature*/);
            return;
        }
        const messageSize = this.getMessageSize(message);
        if (messageSize >= messageMaxSize) {
            this.logEgressActivity(false /*success*/, `Message size exceeded ${messageMaxSize}`, `Message size: ${messageSize}`);
            return;
        }
        if ((message.obj instanceof ArrayBuffer || message.isHttpSessionInitMessage) && this.worker) {
            this.logEgressActivity(true /*success*/, `isArrayBuffer: ${message.obj instanceof ArrayBuffer}, isHttpSessionInitMessage: ${message.isHttpSessionInitMessage}`, '' /*resultSignature*/);
            this.worker.egress(message);
            return;
        }
        this.validateSyncMessage(message.messageId, message.seqId);
        if (this.dynamicRateControllerEnabled) {
            const messageId = (_a = message.messageId) !== null && _a !== void 0 ? _a : `id${this.emptyMessageId++}`;
            const egressMessage = {
                obj: message.obj,
                seqId: message === null || message === void 0 ? void 0 : message.seqId,
                messageId: messageId,
                isHttpSessionInitMessage: message === null || message === void 0 ? void 0 : message.isHttpSessionInitMessage
            };
            const isRetry = attemptNumber > 0;
            const logOperation = new LogOperation({ operationName: 'NetworkRateControllerQueueItem', success: true, dimension3: `isRetry: ${isRetry}` }).start();
            logOperation.setClientMetadata(this.clientMetadata);
            const queueItem = {
                message: egressMessage,
                logOp: logOperation
            };
            logOperation.dimension0 = 'Enqueue';
            // Retry messages are added to the front of the queue
            if (isRetry) {
                this.messageQueue.unshift(queueItem);
            }
            else {
                this.messageQueue.push(queueItem);
            }
            if (this.messageQueue.length > maxQueueSize) {
                this.messageQueue.shift();
                throw new Error('MessageQueue max size reached.');
            }
            Log.info(0x1e3e2283 /* tag_4p8kd */, LogCategory.CoreDefault, logOperation.stop());
            (_b = this.resolveQueueNotEmptyPromise) === null || _b === void 0 ? void 0 : _b.call(this);
        }
        else {
            const egressCacheSize = this.queryEgressCacheSize();
            const isRPSRateExceeded = this.egressMessageCount >= this.rpsThreshold;
            const isBPSRateExceeded = this.egressByteCount >= this.bpsThreshold;
            const isEgressSizeLimitExceeded = egressCacheSize > egressedMaxSize;
            const isConnected = this.reliabilityManager.checkConnection();
            const isPendingQueueNotEmpty = this.pendingQueue.length !== 0;
            const isPendingQueueFull = this.pendingQueue.length >= pendingMaxSize;
            if (!isConnected || isRPSRateExceeded || isBPSRateExceeded || isPendingQueueNotEmpty || isPendingQueueFull || isEgressSizeLimitExceeded) {
                // Do not send through network worker yet if egressRate isn't healthy, we aren't connected, or we have pending messages
                let signature = '';
                const dim = []; // Logging magnitude for COGS
                if (isPendingQueueNotEmpty || isPendingQueueFull) {
                    signature += isPendingQueueFull ? 'Pending queue max exceeded. ' : 'Pending queue not empty. ';
                    dim.push(this.pendingQueue.length.toString().length.toString());
                }
                if (isEgressSizeLimitExceeded) {
                    signature += egressCacheSize >= egressedMaxSize ? 'Egressed cache max exceeded. ' : '';
                    dim.push(egressCacheSize.toString().length.toString());
                }
                if (isRPSRateExceeded) {
                    signature += 'RPS exceeded. ';
                    dim.push(this.egressMessageCount.toString().length.toString());
                }
                if (isBPSRateExceeded) {
                    signature += 'BPS exceeded. ';
                    dim.push(this.egressByteCount.toString().length.toString());
                }
                signature += !isConnected ? 'Not connected. ' : '';
                this.logEgressActivity(true /*success*/, `networkMode: ${this.networkMode}`, signature, dim);
                // Do not send through network worker yet if any of the conditions is true
                const messageId = (_c = message.messageId) !== null && _c !== void 0 ? _c : `id${this.emptyMessageId++}`;
                this.pendingQueue.push({ obj: message.obj, seqId: message === null || message === void 0 ? void 0 : message.seqId, messageId: messageId, isHttpSessionInitMessage: message === null || message === void 0 ? void 0 : message.isHttpSessionInitMessage });
                return;
            }
            this.sendToNetworkWorker(message);
        }
    }
    getMessageSize(message) {
        return message.obj instanceof ArrayBuffer ? message.obj.byteLength : message.obj.length;
    }
    /**
     * Send the message to the network worker. Also cache it and wait to be acked.
     *
     * @param message message to be sent, also containing the messageId
     */
    sendToNetworkWorker(message) {
        try {
            if (!this.worker) {
                this.logEgressActivity(false /*success*/, 'NetworkWorker is undefined', 'SendToNetworkWorkerFailure');
                return;
            }
            if (!this.reliabilityManager) {
                this.logEgressActivity(false /*success*/, 'Reliability Manager is undefined', 'SendToNetworkWorkerFailure');
                return;
            }
            this.worker.egress(message);
            this.reliabilityManager.postEgress();
            // Keep track of egress rate
            const messageSize = this.getMessageSize(message);
            this.logEgressCount(messageSize);
        }
        catch (e) {
            this.logEgressActivity(false /*success*/, 'SendToNetworkWorkerFailure', e ? e.message : '');
        }
    }
    /**
     * Flush out pending messages
     */
    flush() {
        if (!this.reliabilityManager) {
            const flushLogOp = new LogOperation({ operationName: 'NetworkRateControllerFlushFailure', success: false, dimension3: `networkMode: ${this.networkMode}` }).start();
            flushLogOp.setClientMetadata(this.clientMetadata);
            flushLogOp.resultDescription = 'Reliability Manager is undefined';
            Log.info(0x1e44f005 /* tag_4rpaf */, LogCategory.CoreDefault, flushLogOp.stop());
            return;
        }
        let flushedCount = 0;
        let flushedBytes = 0;
        while (this.pendingQueue.length > 0
            && this.queryEgressCacheSize() < egressedMaxSize
            && flushedCount < this.rpsThreshold
            && flushedBytes < this.bpsThreshold
            && this.reliabilityManager.checkConnection()) {
            const message = this.pendingQueue.shift();
            this.sendToNetworkWorker(message);
            flushedCount++;
            flushedBytes += this.getMessageSize(message);
        }
    }
    /**
     * Asynchronously process messages in the queue
     */
    processQueue() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.reliabilityManager) {
                const flushLogOp = new LogOperation({ operationName: 'NetworkRateControllerProcessQueueFailure', success: false, dimension3: `networkMode: ${this.networkMode}` }).start();
                flushLogOp.setClientMetadata(this.clientMetadata);
                flushLogOp.resultDescription = 'Reliability Manager is undefined';
                Log.info(0x1e40f58b /* tag_4qpwl */, LogCategory.CoreDefault, flushLogOp.stop());
                return;
            }
            const logOp = new LogOperation({ operationName: 'NetworkRateControllerProcessQueue', success: true }).start();
            // eslint-disable-next-line no-constant-condition
            while (true) {
                if (this.messageQueue.length === 0) {
                    if (this.isClosing) {
                        logOp.setDataField('ExitReason', 'Stage#1 Closing');
                        break;
                    }
                    yield Promise.race([this.queueNotEmpty(), this.onCloseController]);
                }
                if (this.rateLimitTimeout) {
                    const logOp = new LogOperation({ operationName: 'NetworkRateControllerRateLimitBackoff', dimension3: `networkMode: ${this.networkMode}` }).start();
                    logOp.setClientMetadata(this.clientMetadata);
                    logOp.setDataField('QueueLengthBeforeBackoff', this.messageQueue.length);
                    yield Promise.race([this.rateLimitTimeout, this.onCloseController]);
                    logOp.setDataField('QueueLengthAfterBackoff', this.messageQueue.length);
                    logOp.setDataField('IsClosing', this.isClosing);
                    logOp.success = true;
                    this.rateLimitTimeout = undefined;
                    this.resetRateLimiter();
                    Log.info(0x1e3c8002 /* tag_4piac */, LogCategory.CoreDefault, logOp.stop());
                }
                if (!((_a = this.reliabilityManager) === null || _a === void 0 ? void 0 : _a.checkConnection())) {
                    // Don't wait for connection to become active when close is in progress.
                    if (this.isClosing) {
                        logOp.setDataField('ExitReason', 'Stage#2 Closing');
                        break;
                    }
                    yield this.checkConnectionPromise();
                    this.connectionPromise = undefined;
                }
                if (!this.reliabilityManager) {
                    logOp.setDataField('ExitReason', 'Stage#2 ReliabilityManager null');
                    break;
                }
                while (this.messageQueue.length > 0
                    && this.reliabilityManager.checkConnection()) {
                    const elapsedMs = Date.now() - this.egressRateControlIntervalStart;
                    if (elapsedMs >= rateControllerIntervalMs) {
                        this.resetRateLimiter();
                    }
                    else if (this.egressMessageCount >= this.rpsThreshold
                        || this.egressByteCount >= this.bpsThreshold) { // exceeded rate limit
                        const elapsedMs = Date.now() - this.egressRateControlIntervalStart;
                        const delayMs = rateControllerIntervalMs - elapsedMs + 100; // remaining time in interval with 100ms buffer
                        let signature = '';
                        if (this.egressMessageCount >= this.rpsThreshold) {
                            signature += 'RPS exceeded.';
                        }
                        if (this.egressByteCount >= this.bpsThreshold) {
                            signature += 'BPS exceeded.';
                        }
                        const description = 'rateLimitHit';
                        const logOp = new LogOperation({ operationName: 'NetworkRateControllerEgress', dimension3: `networkMode: ${this.networkMode}` }).start();
                        logOp.setClientMetadata(this.clientMetadata);
                        logOp.success = true;
                        logOp.resultDescription = description;
                        logOp.resultSignature = signature;
                        logOp.dimension0 = this.messageQueue.length.toString().length.toString(); // Logging magnitude for COGS
                        logOp.dimension1 = 'rateLimitHit';
                        logOp.setDataField('QueueLength', this.messageQueue.length);
                        logOp.setDataField('RateLimitDelayMs', delayMs);
                        Log.info(0x1e3c8001 /* tag_4piab */, LogCategory.CoreDefault, logOp.stop());
                        this.startRateLimiting(delayMs);
                        break;
                    }
                    const queueItem = this.messageQueue.shift();
                    queueItem.logOp.dimension0 = 'Dequeue';
                    // sendToNetworkWorker calls logEgressCount which resets egressMessageCount and egressByteCount when elapsed time > 1s
                    this.sendToNetworkWorker(queueItem.message);
                    Log.info(0x1e3e230a /* tag_4p8mk */, LogCategory.CoreDefault, queueItem.logOp.stop());
                }
                this.onQueueNotEmpty = undefined;
                this.resolveQueueNotEmptyPromise = undefined;
            }
            this.resolveQueueProcessingCompletePromise();
            this.resolveQueueProcessingCompletePromise = undefined;
            logOp.setClientMetadata(this.clientMetadata);
            logOp.setDataField('QueueSize', this.messageQueue.length);
            logOp.setDataField('IsConnected', (_b = this.reliabilityManager) === null || _b === void 0 ? void 0 : _b.checkConnection());
            logOp.setDataField('IsClosing', this.isClosing);
            Log.info(0x1e3dd4df /* tag_4p3t5 */, LogCategory.CoreDefault, logOp.stop());
        });
    }
    queueNotEmpty() {
        if (!this.onQueueNotEmpty) {
            this.onQueueNotEmpty = new Promise((resolve) => {
                this.resolveQueueNotEmptyPromise = resolve;
            });
        }
        return this.onQueueNotEmpty;
    }
    checkConnectionPromise() {
        if (!this.connectionPromise) {
            this.connectionPromise = new Promise((resolve) => {
                setTimeout(resolve, 1000);
            });
        }
        return this.connectionPromise;
    }
    startRateLimiting(delayMs) {
        if (!this.rateLimitTimeout) {
            this.rateLimitTimeout = new Promise((resolve) => {
                setTimeout(resolve, delayMs);
            });
        }
    }
    resetRateLimiter() {
        this.egressRateControlIntervalStart = Date.now();
        this.egressMessageCount = 0;
        this.egressByteCount = 0;
    }
    clearEgressControlTimeout() {
        if (this.egressRateControlTimer) {
            clearInterval(this.egressRateControlTimer);
            this.egressRateControlTimer = undefined;
        }
    }
    /**
     * Check for sequence gap in Sync messages.
     * The seq is expected to be continuous.
     *
     * @param messageId message id of the sync message
     * @param seq sequence id of the Sync message
     */
    validateSyncMessage(messageId, seqId) {
        // Skip validation if message or seq id is absent or it is a resent message
        if (seqId === undefined || messageId === undefined || seqId <= this.prevSeq) {
            return;
        }
        if (seqId && seqId !== this.prevSeq + 1) {
            const validateSyncMessageLogOp = new LogOperation({ operationName: 'NetworkRateControllerAbandonedSyncMessage', success: true, dimension3: `networkMode: ${this.networkMode}` }).start();
            validateSyncMessageLogOp.setClientMetadata(this.clientMetadata);
            validateSyncMessageLogOp.resultDescription = 'Gap in sync message';
            validateSyncMessageLogOp.dimension0 = `${this.prevSeq}`;
            validateSyncMessageLogOp.dimension1 = `${seqId}`;
            validateSyncMessageLogOp.dimension2 = `${seqId - this.prevSeq}`;
            Log.info(0x1e44f002 /* tag_4rpac */, LogCategory.CoreDefault, validateSyncMessageLogOp.stop());
        }
        this.prevSeq = seqId;
    }
    logEgressActivity(success, resultDescription, resultSignature, dim) {
        var _a, _b, _c, _d;
        const logOp = new LogOperation({ operationName: 'NetworkRateControllerEgress', dimension3: `networkMode: ${this.networkMode}` }).start();
        logOp.setClientMetadata(this.clientMetadata);
        logOp.success = success;
        logOp.resultDescription = resultDescription;
        logOp.resultSignature = resultSignature;
        if (dim) {
            logOp.dimension0 = (_a = dim[0]) !== null && _a !== void 0 ? _a : '';
            logOp.dimension1 = (_b = dim[1]) !== null && _b !== void 0 ? _b : '';
            logOp.dimension2 = (_c = dim[2]) !== null && _c !== void 0 ? _c : '';
            logOp.dimension3 = (_d = dim[3]) !== null && _d !== void 0 ? _d : '';
        }
        Log.info(0x1e41c217 /* tag_4q2ix */, LogCategory.CoreDefault, logOp.stop());
    }
    logEgressCount(byteCount) {
        const now = Date.now();
        if (now - this.egressRateControlIntervalStart > rateControllerIntervalMs) {
            if (this.egressMessageCount > rpsLoggingThreshold || this.egressByteCount > bpsLoggingThreshold) {
                this.egressRateLogOp.start();
                this.egressRateLogOp.resultDescription = this.egressMessageCount > rpsLoggingThreshold ? 'rps logging threshold exceeded' : '';
                this.egressRateLogOp.resultDescription = this.egressByteCount > bpsLoggingThreshold ? 'bps logging threshold exceeded' : '';
                this.egressRateLogOp.dimension0 = (now - this.egressRateControlIntervalStart).toString();
                this.egressRateLogOp.dimension1 = `${this.egressMessageCount}`;
                this.egressRateLogOp.dimension2 = `${this.egressByteCount}`;
                this.egressRateLogOp.dimension3 = `networkMode: ${this.networkMode}`;
                Log.info(0x1e545710 /* tag_4vf2q */, LogCategory.CoreDefault, this.egressRateLogOp.stop());
            }
            this.egressRateControlIntervalStart = now;
            this.egressMessageCount = 0;
            this.egressByteCount = 0;
        }
        this.egressMessageCount++;
        this.egressByteCount += (byteCount !== null && byteCount !== void 0 ? byteCount : 0);
    }
}
//# sourceMappingURL=network-rate-controller.js.map